import type { GetPlansOverviewsResponse } from '@weft/api-definition';
import { GetPlansOverviewsResponseSchema } from '@weft/api-definition';
import { isNotEmptyString, purge, withDefault } from '@weft/utils';
import { getPrivateApi } from 'apps/app/src/services/api';
import type { QueryClient } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { usePlansOverviewsStatusesParam } from '../../../pages/plans/PlansPage/plans-page-components/hooks/use-plans-overviews-statuses-param';
import { usePageParam } from '../../search-params/use-page-param';
import { usePageSizeParam } from '../../search-params/use-page-size-param';
import { useSearchWithSearchParams } from '../../use-search-with-search-params';
import type { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';
import { PLANS_OVERVIEWS_PAGE_SIZE } from './use-plans-overviews-consts';

type UsePlansOverviewsPayload = Parameters<ReturnType<typeof getPrivateApi>['getPlanOverviews']>[0];

export function usePlansOverviews(payload: UsePlansOverviewsPayload = {}, options?: CustomUseQueryOptions<GetPlansOverviewsResponse>) {
  const queryClient = useQueryClient();

  return useQuery(['plansOverviews', ...purge(Object.values(payload))], async ({ signal }) => {
    const api = getPrivateApi();
    await cancelGetPlansOverviewsQueries(queryClient);

    const getPlansOverviewsResponse = await api.getPlanOverviews({
      ...payload,
      pageSize: withDefault(payload.pageSize, PLANS_OVERVIEWS_PAGE_SIZE),
      page: withDefault(payload.page, 0),
    }, { signal });

    return GetPlansOverviewsResponseSchema.parse(getPlansOverviewsResponse);
  }, {
    keepPreviousData: true,
    ...options,
  });
}

async function cancelGetPlansOverviewsQueries(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === 'plansOverviews',
  });
}

export function usePlansOverviewsWithPageParams(options?: CustomUseQueryOptions<GetPlansOverviewsResponse>) {
  const [page] = usePageParam();
  const [pageSize] = usePageSizeParam();
  const [plansOverviewsStatuses, _setPlansOverviewsStatuses] = usePlansOverviewsStatusesParam();
  const [query] = useSearchWithSearchParams();

  return usePlansOverviews({
    page,
    pageSize,
    search: isNotEmptyString(query) ? query : undefined,
    planStatuses: JSON.stringify(withDefault(plansOverviewsStatuses, [])),
  }, options);
}